import { useState } from "react";
import type { Authorization, DeliverySettings, Zone } from "../../api/types";
import { useTill } from "../../app/till-context";
import { t } from "../../i18n";
import { formatEuro } from "../../lib/money";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { HoursTable } from "../../ui/HoursTable";
import { SaveInput } from "../../ui/SaveInput";
import { ZoneDialog } from "./ZoneDialog";
import { amountText, kmText, readAmount } from "./zones";

interface Props {
  /** The owner's Beheer code. */
  authorization: Authorization;
}

const EVENING: [string, string] = ["17:00", "22:45"];

/**
 * Beheer › Leveringszones, as in the prototype: the standard fee and "gratis vanaf", the zones per
 * (deel)gemeente (on/off, minimum, own fee, distance), and the delivery hours. Every change is saved
 * at once and holds at once at the till.
 */
export function ZonesScreen({ authorization }: Props) {
  const { api, delivery, deliveryChanged, report, toast } = useTill();
  const [open, setOpen] = useState<{ zone: Zone | null } | null>(null);
  const [removing, setRemoving] = useState<Zone | null>(null);
  const [busy, setBusy] = useState(false);
  const text = t.zonesAdmin;
  if (delivery === null) return <div className="panel admin-section" aria-busy="true" />;
  const settings = delivery;

  const save = async (next: DeliverySettings, message?: string): Promise<boolean> => {
    // shown at once (a checkbox follows the tap); the server's answer follows
    deliveryChanged(next);
    setBusy(true);
    try {
      const answer = await api.request<{ delivery: DeliverySettings }>("PUT", "/device/delivery", {
        staffToken: authorization.token,
        body: next,
      });
      deliveryChanged(answer.delivery);
      if (message) toast(message);
      return true;
    } catch (error) {
      deliveryChanged(settings);
      report(error);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const withZone = (zone: Zone) =>
    settings.zones.some((other) => other.id === zone.id)
      ? settings.zones.map((other) => (other.id === zone.id ? zone : other))
      : [...settings.zones, zone];

  const standardFree = settings.freeFromCents === null ? text.never : formatEuro(settings.freeFromCents);

  return (
    <div className="panel admin-section zones-admin">
      <h2>{text.title}</h2>
      <div className="zbox">
        <h3>{text.standard}</h3>
        <div className="inl zinl">
          <span className="pc-only">{text.fee}</span>
          <span className="phone-only">{text.feeShort}</span>{" "}
          <SaveInput
            value={amountText(settings.feeCents)}
            label={text.fee}
            className="num"
            inputMode="decimal"
            onSave={(value) => {
              const cents = readAmount(value);
              if (cents !== undefined) void save({ ...settings, feeCents: cents ?? 0 });
            }}
          />{" "}
          <span>{text.freeFrom}</span>{" "}
          <SaveInput
            value={amountText(settings.freeFromCents)}
            label={text.freeFrom}
            placeholder={text.never}
            className="num"
            inputMode="decimal"
            onSave={(value) => {
              const cents = readAmount(value);
              if (cents !== undefined) void save({ ...settings, freeFromCents: cents });
            }}
          />
        </div>
      </div>
      {settings.zones.length === 0 ? (
        <p className="note">{text.none}</p>
      ) : (
        <div className="tablewrap">
          <table className="stafftbl zonetbl stack">
            <thead>
              <tr>
                <th>{text.columns.on}</th>
                <th>{text.columns.postcode}</th>
                <th>{text.columns.zone}</th>
                <th className="r">{text.columns.minimum}</th>
                <th className="r">{text.columns.fee}</th>
                <th className="r">{text.columns.freeFrom}</th>
                <th>{text.columns.far}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {settings.zones.map((zone) => {
                const far = text.farText(
                  zone.far.fromKm === null ? null : kmText(zone.far.fromKm),
                  zone.far.extraCents === null ? null : formatEuro(zone.far.extraCents),
                  zone.far.maxKm === null ? null : kmText(zone.far.maxKm),
                );
                return (
                  <tr key={zone.id} className={zone.on ? "" : "dim"}>
                    <td>
                      <input
                        type="checkbox"
                        className="zon"
                        checked={zone.on}
                        disabled={busy}
                        aria-label={text.onOff(zone.name)}
                        onChange={(event) => {
                          const on = event.target.checked;
                          void save(
                            { ...settings, zones: withZone({ ...zone, on }) },
                            on ? text.turnedOn(zone.name) : text.turnedOff(zone.name),
                          );
                        }}
                      />
                    </td>
                    <td className="num">{zone.postcode}</td>
                    <td className="grow">
                      <b>{zone.name}</b>
                    </td>
                    <td className="r num later" data-label={text.columns.minimum}>
                      {formatEuro(zone.minimumCents)}
                    </td>
                    <td className="r num later" data-label={text.columns.fee}>
                      {zone.feeCents === null ? (
                        <span className="note">{formatEuro(settings.feeCents)}</span>
                      ) : (
                        formatEuro(zone.feeCents)
                      )}
                    </td>
                    <td className="r num later" data-label={text.columns.freeFrom}>
                      {zone.freeFromCents === null ? (
                        <span className="note">{standardFree}</span>
                      ) : (
                        formatEuro(zone.freeFromCents)
                      )}
                    </td>
                    <td className="later" data-label={text.columns.far}>
                      {far || <span className="note">—</span>}
                    </td>
                    <td className="end">
                      <button
                        type="button"
                        className="btn"
                        aria-label={text.editOf(zone.name)}
                        onClick={() => {
                          setOpen({ zone });
                        }}
                      >
                        {text.edit}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="rowacts zadd">
        <button
          type="button"
          className="btn primary"
          onClick={() => {
            setOpen({ zone: null });
          }}
        >
          {text.add}
        </button>
      </div>

      <div className="zbox">
        <h3>{text.hours}</h3>
        <HoursTable
          hours={settings.hours}
          reopen={EVENING}
          disabled={busy}
          onChange={(hours) => void save({ ...settings, hours })}
        />
        <p className="note">{text.hoursNote}</p>
      </div>

      {open && (
        <ZoneDialog
          settings={settings}
          zone={open.zone}
          busy={busy}
          onSave={(zone) => {
            void save({ ...settings, zones: withZone(zone) }, text.dialog.saved(zone.name)).then((ok) => {
              if (ok) setOpen(null);
            });
          }}
          onRemove={(zone) => {
            setOpen(null);
            setRemoving(zone);
          }}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
      {removing && (
        <ConfirmDialog
          message={text.dialog.removeConfirm(removing.name)}
          confirm={text.dialog.remove}
          onConfirm={() => {
            const zone = removing;
            setRemoving(null);
            void save(
              { ...settings, zones: settings.zones.filter((other) => other.id !== zone.id) },
              text.dialog.removed(zone.name),
            );
          }}
          onClose={() => {
            setRemoving(null);
          }}
        />
      )}
    </div>
  );
}
